import { useState, useEffect, useMemo } from "react";
import Accounts from "./subpages/Accounts";
import Security from "./subpages/Security";
import Notification from "./subpages/Notification";
import Profile from "./subpages/Profile";
import Quota from "./subpages/Quota";
import Subscribe from "./subpages/Subscribe";
import settingsApi from '../../api/settingsApi'
import dashboardApi from '../../api/dashboardApi'
import stripeApi from '../../api/stripeApi'
import qboApi from '../../api/qboApi'
import businessApi from '../../api/businessApi'
import notificationApi from '../../api/notificationApi'
import Alert from '../../components/Alert'
import IndustrySelectionModal from './components/IndustrySelectionModal';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../context/UserContext';
import { useWorkspace } from '../../context/WorkspaceContext';

const REFETCH_TIME = 1000 * 60 * 20; // Force refetch every 20 mins
const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';

export default function SettingsPage() {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // Use UserContext for metadata and admin status
    // Note: userMetadata can be null. Components expect undefined if not present.
    // Convert null to undefined using ?? undefined or || undefined.
    const { userMetadata, isLoading: isMetadataLoading, refetch: refetchMetadata, isAdmin } = useUser();
    const { activeBusinessId } = useWorkspace();
    const queryClient = useQueryClient();

    // Dynamic tabs logic: Restore dev mode structure + Admin filter
    const baseTabs = isDevMode
        ? ["Notification", "Connectors", "Billing", "Usage", "Profile"]
        : ["Notification", "Connectors", "Billing", "Usage", "Profile"];

    const tabs = isAdmin
        ? baseTabs.filter(t => t !== "Notification")
        : baseTabs;

    const defaultTab = isDevMode ? "Connectors" : "Connectors";

    // Tab State: prioritized from URL ?tab=...
    const urlTabParam = searchParams.get("tab") || "";
    // Match URL param to a tab case-insensitively
    const matchedTab = tabs.find(t => t.toLowerCase() === urlTabParam.toLowerCase());

    // Fallbacks: Exact match -> Case-insensitive match -> Is success=true? -> state -> default
    const isSuccessRedirect = searchParams.get("success") === "true";
    const activeTab = matchedTab || (isSuccessRedirect ? "Billing" : (location.state?.activeTab || defaultTab));

    // Helper to safely set tab
    const handleTabChange = (newTab: string) => {
        setSearchParams({ tab: newTab });
    };

    // Stripe Balances Data
    const {
        data: stripeData,
        error: stripeError,
        refetch: refetchStripeData,
    } = useQuery({
        queryKey: ['stripeData', activeBusinessId],
        queryFn: () => stripeApi.getLinkedAccounts(activeBusinessId || undefined),
        refetchInterval: REFETCH_TIME,
        retry: false,
    });

    // Qbo Balances Data
    const {
        data: qboData,
        refetch: refetchQboData,
    } = useQuery({
        queryKey: ['qboData'],
        queryFn: dashboardApi.getQboAccounts,
        refetchInterval: REFETCH_TIME,
        retry: false,
    });

    // Profile Data
    const {
        data: profileData,
        refetch: refetchProfileData,
    } = useQuery({
        queryKey: ['profileData'],
        queryFn: settingsApi.getProfile,
        refetchInterval: REFETCH_TIME,
        retry: false,
    });

    // Force refetch metadata when entering the page or switching tabs
    useEffect(() => {
        refetchMetadata();
    }, [activeTab, refetchMetadata]);

    const { data: businessesData } = useQuery({
        queryKey: ['businesses'],
        queryFn: businessApi.getBusinesses,
        retry: false,
    });

    const { data: notificationFeed } = useQuery({
        queryKey: ['notification-feed', activeBusinessId],
        queryFn: () => notificationApi.getFeed(activeBusinessId || undefined),
        enabled: !isAdmin,
        retry: false,
    });

    const [showIndustryModal, setShowIndustryModal] = useState(false);

    const activeBusiness = useMemo(() => {
        if (!activeBusinessId || !businessesData) return null;
        return businessesData?.data?.businesses?.find((biz: any) => biz.id === activeBusinessId) || null;
    }, [businessesData, activeBusinessId]);

    const isRestaurantBusiness = useMemo(() => {
        if (!activeBusiness) return false;
        const industry = (activeBusiness.industry || '').toLowerCase();
        const businessType = (activeBusiness.business_type || '').toLowerCase();
        const naicsCode = String(activeBusiness.naics_code || '');
        return (
            industry === 'restaurant' ||
            naicsCode.startsWith('722') ||
            /restaurant|cater|food truck|bakery|cafe|coffee shop/.test(businessType)
        );
    }, [activeBusiness]);

    useEffect(() => {
        if (!activeBusiness) {
            setShowIndustryModal(false);
            return;
        }

        const isIndustryMissing = !activeBusiness?.industry || !activeBusiness?.business_type || !activeBusiness?.naics_code;
        const isRestaurantProfileMissing = isRestaurantBusiness && (!activeBusiness?.restaurant_type || !activeBusiness?.restaurant_size);

        if (activeTab === "Connectors" && (isIndustryMissing || isRestaurantProfileMissing)) {
            setShowIndustryModal(true);
            return;
        }
        setShowIndustryModal(false);
    }, [activeBusiness, activeTab, isRestaurantBusiness]);

    const handleIndustrySuccess = async () => {
        await Promise.all([
            refetchMetadata(),
            queryClient.invalidateQueries({ queryKey: ['businesses'] }),
            queryClient.invalidateQueries({ queryKey: ['workspaceContext'] }),
        ]);
        setShowIndustryModal(false);
    };

    const handleSaveEdit = async () => {
        await refetchProfileData();
    };

    const handleStripeLink = async () => {
        await stripeApi.link(activeBusinessId || undefined);
        await refetchStripeData();
        await refetchMetadata(); // Refetch metadata after linking
    };

    const handleQboLink = async () => {
        const { auth_url } = await qboApi.initiateQboConnection(activeBusinessId || undefined);
        if (auth_url) window.location.href = auth_url;
        await refetchQboData();
        await refetchMetadata(); // Refetch metadata after linking
    };

    const refreshAll = async () => {
        await Promise.all([
            refetchStripeData(),
            refetchQboData(),
            refetchMetadata()
        ]);
    };

    const renderTab = () => {
        switch (activeTab) {
            case "Connectors":
                return <Accounts
                    stripeData={stripeData}
                    stripeError={stripeError ? (stripeError instanceof Error ? stripeError.message : 'Failed to load connected bank accounts.') : undefined}
                    qboData={qboData && qboData.items}
                    userMetadata={userMetadata ?? undefined}
                    handleStripeLink={handleStripeLink}
                    handleQboLink={handleQboLink}
                    refetchData={refreshAll} />;
            case "Security":
                return <Security />;
            case "Notification":
                if (isAdmin) return null;
                return <Notification />;
            case "Usage":
                return <Quota />;
            case "Billing":
                return <Subscribe />;
            case "Profile":
                if (!profileData) {
                    return (
                        <div className="mt-10">
                            <Alert subtitle={""} />
                        </div>)
                }
                return <Profile data={profileData} handleSaveEdit={handleSaveEdit} />;
            default:
                // Fallback to default tab content if invalid param
                // By making matching case-insensitive above, this should rarely hit unless param is total garbage.
                // In that case, let's just render the default
                return <Accounts
                    stripeData={stripeData}
                    stripeError={stripeError ? (stripeError instanceof Error ? stripeError.message : 'Failed to load connected bank accounts.') : undefined}
                    qboData={qboData && qboData.items}
                    userMetadata={userMetadata ?? undefined}
                    handleStripeLink={handleStripeLink}
                    handleQboLink={handleQboLink}
                    refetchData={refreshAll} />;
        }
    };

    return (
        <div className="w-full h-full bg-white p-4 md:p-6 flex flex-col overflow-hidden max-w-full">
            {/* Top Tab Nav */}
            <div className="flex space-x-8 mb-6 shrink-0 overflow-x-auto scrollbar-hide">
                {tabs.map((tab) => {
                    // Notification badge reflects only unread notification feed items.
                    const getNotificationCount = () => {
                        if (!userMetadata || isAdmin) return 0;
                        return notificationFeed?.unread_count || 0;
                    };

                    const notificationCount = getNotificationCount();
                    const showBadge = tab === "Notification" && !isMetadataLoading && notificationCount > 0;

                    return (
                        <button
                            key={tab}
                            className={`pb-2 font-semibold flex items-center gap-2 shrink-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${activeTab === tab
                                ? "border-b-2 border-indigo-400 text-black"
                                : "text-gray-500"
                                }`}
                            onClick={() => handleTabChange(tab)}
                        >
                            {tab}
                            {showBadge && (
                                <div className="bg-red-500 text-white text-[10px] font-bold px-1.5 h-4 flex items-center justify-center rounded-full">
                                    {notificationCount}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Subpage Rendered Here */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide">{renderTab()}</div>

            <IndustrySelectionModal
                isOpen={showIndustryModal}
                onClose={() => setShowIndustryModal(false)}
                onSuccess={handleIndustrySuccess}
                initialValues={activeBusiness || undefined}
            />
        </div>
    );
}
